import { useState } from 'react';
import { t } from '@app/i18n';
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  Drawer,
  DrawerFooter,
  DrawerPresence,
  ErrorBanner,
  Field,
  FieldGrid,
  IconButton,
  LoadingButton,
  TextInput,
  type DataTableColumn,
} from '@zarya/ui';
import { useCanOperar } from '@app/auth/useCan';
import { EnvironmentApprovalSummary } from '../ApprovalWorkflow/EnvironmentApprovalSummary';
import {
  useCreateEnvironment,
  useDeleteEnvironment,
  useEnvironments,
  useUpdateEnvironment,
} from './TenantSettings.api';
import type { ApiError } from '@app/api/ApiError';
import type { Environment, EnvironmentInput } from './TenantSettingsModel';
import s from './TenantSettings.module.css';

/**
 * Cadastro de Settings > Environments: metadados em drawer, aprovação somente
 * como resumo e link para Approval Workflow. O PUT de metadados continua enviando
 * a linha inteira para preservar selecionabilidade, padrão, sinônimos e posição.
 * Os botões de escrita são omitidos para os papéis sem a operação correspondente.
 */
export function EnvironmentsCard() {
  const lista = useEnvironments();
  const canCreate = useCanOperar('createEnvironment');
  const canEdit = useCanOperar('updateEnvironment');
  const canDelete = useCanOperar('deleteEnvironment');

  const criar = useCreateEnvironment();
  const editar = useUpdateEnvironment();
  const remover = useDeleteEnvironment();

  const [aberto, setAberto] = useState<Environment | 'novo' | null>(null);
  const [excluindo, setExcluindo] = useState<Environment | null>(null);

  const itens = lista.data?.items ?? [];

  const columns: DataTableColumn<Environment>[] = [
    {
      key: 'slug',
      header: t('tenantSettings.environments.column.slug'),
      mono: true,
      width: '16%',
      cell: (e) => (
        <span className={s.description}>
          <span>{e.slug}</span>
          {e.is_default ? (
            <Badge tone="primary">{t('tenantSettings.environments.default')}</Badge>
          ) : null}
        </span>
      ),
    },
    {
      key: 'name',
      header: t('tenantSettings.environments.column.name'),
      width: '20%',
      cell: (e) => e.name,
    },
    {
      key: 'synonyms',
      header: t('tenantSettings.environments.column.synonyms'),
      tip: t('tenantSettings.environments.column.synonyms.tip'),
      cell: (e) =>
        e.synonyms.length > 0 ? (
          <span className={s.description}>{e.synonyms.join(', ')}</span>
        ) : (
          <em className={s.semDescricao}>{t('tenantSettings.environments.noSynonyms')}</em>
        ),
    },
    {
      key: 'approval',
      header: t('tenantSettings.environments.column.approval'),
      tip: t('tenantSettings.environments.column.approval.tip'),
      width: '18%',
      // A autoridade vem das três regras efetivas, nunca do booleano legado.
      cell: (e) => <EnvironmentApprovalSummary environment={e} />,
    },
    ...(canEdit || canDelete
      ? [
          {
            key: 'actions',
            header: t('tenantSettings.environments.column.actions'),
            srOnlyHeader: true,
            width: '12%',
            cell: (e: Environment) => (
              <span className={s.description}>
                {canEdit ? (
                  <IconButton
                    icon="pen-square"
                    size="sm"
                    label={t('tenantSettings.environments.edit.label', { name: e.name })}
                    onClick={() => {
                      editar.reset();
                      setAberto(e);
                    }}
                  />
                ) : null}
                {canDelete ? (
                  <IconButton
                    icon="trash"
                    size="sm"
                    danger
                    label={t('tenantSettings.environments.delete.label', { name: e.name })}
                    onClick={() => {
                      remover.reset();
                      setExcluindo(e);
                    }}
                  />
                ) : null}
              </span>
            ),
          } satisfies DataTableColumn<Environment>,
        ]
      : []),
  ];

  return (
    <Card
      title={t('tenantSettings.environments.title')}
      subtitle={t('tenantSettings.environments.subtitle')}
      toolbar={
        canCreate ? (
          <Button
            variant="out"
            icon="plus"
            onClick={() => {
              criar.reset();
              setAberto('novo');
            }}
          >
            {t('tenantSettings.environments.new')}
          </Button>
        ) : null
      }
    >
      <DataTable<Environment>
        label={t('tenantSettings.environments.table.label')}
        columns={columns}
        rows={itens}
        rowKey={(e) => e.id}
        loading={lista.isLoading}
        error={lista.error}
        onRetry={() => void lista.refetch()}
        emptyTitle={t('tenantSettings.environments.empty.title')}
        emptyMessage={t('tenantSettings.environments.empty.message')}
      />

      <DrawerPresence>
        {aberto ? (
          <EnvironmentDrawer
            atual={aberto === 'novo' ? null : aberto}
            proximaPosicao={itens.reduce((max, e) => Math.max(max, e.position), 0) + 1}
            pending={criar.isPending || editar.isPending}
            error={criar.error ?? editar.error}
            onClose={() => {
              criar.reset();
              editar.reset();
              setAberto(null);
            }}
            onSave={(body) => {
              if (aberto === 'novo') {
                criar.mutate(body, { onSuccess: () => setAberto(null) });
              } else {
                editar.mutate({ id: aberto.id, body }, { onSuccess: () => setAberto(null) });
              }
            }}
          />
        ) : null}
      </DrawerPresence>

      <ConfirmDialog
        open={excluindo !== null}
        title={t('tenantSettings.environments.confirm.title', { name: excluindo?.name ?? '' })}
        message={
          excluindo?.is_default
            ? t('tenantSettings.environments.confirm.isDefault')
            : t('tenantSettings.environments.confirm.message')
        }
        destructive
        confirmLabel={t('tenantSettings.environments.confirm.action')}
        // O padrão não tem saída nenhuma por aqui: o servidor recusa com
        // `environment_is_default` e a saída é marcar outro primeiro. Desabilitar é
        // dizer isso antes de gastar a viagem.
        confirmDisabled={Boolean(excluindo?.is_default)}
        loading={remover.isPending}
        error={
          remover.error ? (
            <ErrorBanner
              error={remover.error}
              escrita
              title={t('tenantSettings.environments.confirm.error')}
              hideLink
            />
          ) : null
        }
        onCancel={() => {
          setExcluindo(null);
          remover.reset();
        }}
        onConfirm={() => {
          if (!excluindo) return;
          remover.mutate(excluindo.id, { onSuccess: () => setExcluindo(null) });
        }}
      />

      {remover.isSuccess && !excluindo ? (
        <Alert tone="info" title={t('tenantSettings.environments.deleted')} />
      ) : null}
    </Card>
  );
}

/**
 * Drawer de criar/editar. Nasce com a linha INTEIRA quando edita — ver o cabeçalho
 * do card sobre por que isso não é zelo, é a diferença entre preservar e apagar
 * autoridade.
 */
function EnvironmentDrawer({
  atual,
  proximaPosicao,
  pending,
  error,
  onSave,
  onClose,
}: {
  atual: Environment | null;
  proximaPosicao: number;
  pending: boolean;
  error: ApiError | null;
  onSave: (body: EnvironmentInput) => void;
  onClose: () => void;
}) {
  const [slug, setSlug] = useState(atual?.slug ?? '');
  const [name, setName] = useState(atual?.name ?? '');
  const [synonyms, setSynonyms] = useState((atual?.synonyms ?? []).join(', '));
  const [position, setPosition] = useState(String(atual?.position ?? proximaPosicao));
  const [isDefault, setIsDefault] = useState(atual?.is_default ?? false);
  const [selectable, setSelectable] = useState(atual?.selectable_for_identity ?? true);

  const dirty =
    slug !== (atual?.slug ?? '') ||
    name !== (atual?.name ?? '') ||
    synonyms !== (atual?.synonyms ?? []).join(', ') ||
    position !== String(atual?.position ?? proximaPosicao) ||
    isDefault !== (atual?.is_default ?? false) ||
    selectable !== (atual?.selectable_for_identity ?? true);

  // Só ambiente selecionável pode ser o padrão (regra do contrato, e do servidor).
  // Desmarcar «selecionável» com «padrão» ligado desliga os dois — é o que impede o
  // 422 que o servidor devolveria, e diz porquê na dica do campo.
  const alternarSelecionavel = (valor: boolean) => {
    setSelectable(valor);
    if (!valor) setIsDefault(false);
  };

  const submeter = () => {
    onSave({
      slug: slug.trim(),
      name: name.trim(),
      synonyms: synonyms
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean),
      position: Number(position),
      is_default: isDefault,
      selectable_for_identity: selectable,
    });
  };

  return (
    <Drawer
      open
      title={
        atual
          ? t('tenantSettings.environments.drawer.edit', { name: atual.name })
          : t('tenantSettings.environments.drawer.new')
      }
      subtitle={t('tenantSettings.environments.drawer.subtitle')}
      dirty={dirty}
      onClose={onClose}
      footer={
        <DrawerFooter>
          <LoadingButton
            loading={pending}
            disabled={!slug.trim() || !name.trim()}
            onClick={submeter}
          >
            {t('tenantSettings.environments.drawer.save')}
          </LoadingButton>
        </DrawerFooter>
      }
    >
      {error ? (
        <ErrorBanner
          error={error}
          escrita
          title={t('tenantSettings.environments.drawer.error')}
          hideLink
        />
      ) : null}

      <FieldGrid>
        <Field
          label={t('tenantSettings.environments.form.slug')}
          span={6}
          tip={t('tenantSettings.environments.form.slug.tip')}
        >
          {(props) => (
            <TextInput
              {...props}
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="dev"
            />
          )}
        </Field>

        <Field label={t('tenantSettings.environments.form.name')} span={6}>
          {(props) => (
            <TextInput {...props} value={name} onChange={(e) => setName(e.target.value)} />
          )}
        </Field>

        <Field
          label={t('tenantSettings.environments.form.synonyms')}
          span={8}
          tip={t('tenantSettings.environments.form.synonyms.tip')}
        >
          {(props) => (
            <TextInput
              {...props}
              value={synonyms}
              onChange={(e) => setSynonyms(e.target.value)}
              placeholder="staging, homologacao"
            />
          )}
        </Field>

        <Field
          label={t('tenantSettings.environments.form.position')}
          span={4}
          tip={t('tenantSettings.environments.form.position.tip')}
        >
          {(props) => (
            <TextInput
              {...props}
              type="number"
              value={position}
              onChange={(e) => setPosition(e.target.value)}
            />
          )}
        </Field>

        <Field
          label={t('tenantSettings.environments.form.selectable')}
          span={6}
          tip={t('tenantSettings.environments.form.selectable.tip')}
        >
          {(props) => (
            <span className={s.toggle}>
              <input
                id={props.id}
                type="checkbox"
                role="switch"
                aria-label={t('tenantSettings.environments.form.selectable')}
                aria-checked={selectable}
                checked={selectable}
                onChange={(e) => alternarSelecionavel(e.target.checked)}
              />
              <span data-testid="ambiente-selecionavel">
                {t(
                  selectable
                    ? 'tenantSettings.environments.form.selectable.yes'
                    : 'tenantSettings.environments.form.selectable.no',
                )}
              </span>
            </span>
          )}
        </Field>

        <Field
          label={t('tenantSettings.environments.form.default')}
          span={6}
          tip={t('tenantSettings.environments.form.default.tip')}
        >
          {(props) => (
            <span className={s.toggle}>
              <input
                id={props.id}
                type="checkbox"
                role="switch"
                aria-label={t('tenantSettings.environments.form.default')}
                aria-checked={isDefault}
                checked={isDefault}
                disabled={!selectable}
                onChange={(e) => setIsDefault(e.target.checked)}
              />
              <span data-testid="ambiente-padrao">
                {t(
                  isDefault
                    ? 'tenantSettings.environments.form.default.yes'
                    : 'tenantSettings.environments.form.default.no',
                )}
              </span>
            </span>
          )}
        </Field>
      </FieldGrid>
    </Drawer>
  );
}
